import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { api } from '../App';
import { toast } from 'sonner';
import {
  Sparkles, Check, ArrowRight, DollarSign, Share2,
  Users, TrendingUp, Zap, Gift, Globe
} from 'lucide-react';

const PartnersLanding = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    country: '',
    paypal_email: '',
    agreed_terms: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.agreed_terms) {
      toast.error('Please agree to the Affiliate Terms.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/partners/register', form);
      toast.success('Partner account created!');
      navigate(`/partners/dashboard?code=${res.data.partner_code}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const commissions = [
    { level: 'A2', price: '$147', rate: '20%', earn: '$29.40', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    { level: 'B1', price: '$197', rate: '20%', earn: '$39.40', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    { level: 'B2', price: '$247', rate: '20%', earn: '$49.40', color: 'bg-purple-100 text-purple-800 border-purple-300' },
    { level: 'Coaching', price: '$497', rate: '20%', earn: '$99.40', color: 'bg-amber-100 text-amber-800 border-amber-300' }
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              DELF Success Academy
            </span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="font-medium">Back to Home</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20">
            <DollarSign className="w-4 h-4" />
            DELF Success Partners
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            Become a DELF Success Partner
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Help students achieve DELF success while earning generous commissions.
          </p>
          {!showForm && (
            <Button
              size="lg"
              className="rounded-full px-10 py-6 font-bold text-[15px] shadow-lg shadow-primary/25"
              onClick={() => setShowForm(true)}
              data-testid="become-partner-cta"
            >
              Become a Partner
            </Button>
          )}
        </motion.div>

        {/* Why Join */}
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8" style={{ fontFamily: 'Syne, sans-serif' }}>
            Why Join?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: DollarSign, text: 'Earn commissions on every successful referral', bg: 'bg-green-50', ic: 'text-green-600' },
              { icon: Users, text: 'Share a proven DELF preparation platform', bg: 'bg-blue-50', ic: 'text-blue-600' },
              { icon: Zap, text: 'No experience required', bg: 'bg-amber-50', ic: 'text-amber-600' },
              { icon: TrendingUp, text: 'Unlimited earning potential', bg: 'bg-purple-50', ic: 'text-purple-600' },
              { icon: Gift, text: 'Free to join', bg: 'bg-rose-50', ic: 'text-rose-600' },
              { icon: Globe, text: 'Works worldwide — share from anywhere', bg: 'bg-sky-50', ic: 'text-sky-600' }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-border/50 flex items-start gap-4">
                <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center shrink-0`}>
                  <item.icon className={`w-5 h-5 ${item.ic}`} />
                </div>
                <span className="text-sm font-medium mt-2">{item.text}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Commission Structure */}
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>
            Commission Structure
          </h2>
          <p className="text-center text-muted-foreground mb-8">Earn 20% on every successful referral</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {commissions.map((c) => (
              <div key={c.level} className={`rounded-xl border p-5 text-center ${c.color}`}>
                <p className="font-bold text-lg">{c.level}</p>
                <p className="text-sm opacity-80 mb-2">Price: {c.price}</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>{c.earn}</p>
                <p className="text-xs font-medium opacity-70">per sale</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* How It Works */}
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8" style={{ fontFamily: 'Syne, sans-serif' }}>
            How It Works
          </h2>
          <div className="flex flex-col items-center gap-2">
            {[
              { step: '1', icon: Gift, title: 'Join for Free', desc: 'Create your partner account in under 60 seconds.' },
              { step: '2', icon: Share2, title: 'Share Your Referral Link', desc: 'Send your unique link to friends, students, or your audience.' },
              { step: '3', icon: Users, title: 'Your Student Enrolls', desc: 'When someone signs up through your link, we track it automatically.' },
              { step: '4', icon: DollarSign, title: 'Earn 20% Commission', desc: 'You earn a generous commission on every successful enrollment.' },
              { step: '5', icon: TrendingUp, title: 'Receive Your Payout', desc: 'Withdraw your earnings to PayPal once you reach $50.' }
            ].map((s, i, arr) => (
              <React.Fragment key={s.step}>
                <div className="w-full max-w-md bg-white rounded-xl p-5 border border-border/50 flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center shrink-0">
                    {s.step}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-sm">{s.title}</h4>
                    <p className="text-xs text-muted-foreground">{s.desc}</p>
                  </div>
                  <s.icon className="w-5 h-5 text-primary/40 shrink-0" />
                </div>
                {i < arr.length - 1 && (
                  <div className="text-primary/30 text-lg font-bold">↓</div>
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.section>

        {/* Estimated Earnings */}
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>
            Potential Earnings
          </h2>
          <p className="text-center text-muted-foreground text-sm mb-8">See what you could earn by sharing DELF Success Academy</p>
          <div className="grid sm:grid-cols-3 gap-5 max-w-2xl mx-auto">
            {[
              { students: '5', amount: '$497' },
              { students: '10', amount: '$994' },
              { students: '20', amount: '$1,988' }
            ].map((e) => (
              <div key={e.students} className="bg-white rounded-xl p-6 border border-border/50 text-center">
                <p className="text-sm text-muted-foreground mb-1">Refer</p>
                <p className="text-3xl font-bold text-primary mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>{e.students}</p>
                <p className="text-sm text-muted-foreground mb-3">Students</p>
                <p className="text-xs text-muted-foreground">Earn up to</p>
                <p className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>{e.amount}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-4 italic">
            Examples are based on successful enrollments and may vary.
          </p>
        </motion.section>

        {/* Registration Form */}
        {showForm && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
            data-testid="partner-registration-form"
          >
            {/* Trust Banner */}
            <div className="max-w-lg mx-auto bg-primary/5 border border-primary/20 rounded-2xl p-6 mb-6 text-center">
              <p className="font-semibold text-sm mb-2">No joining fees. No hidden costs. No selling required.</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Simply recommend DELF Success Academy and earn commissions whenever your referrals successfully enroll.
              </p>
            </div>

            <div className="max-w-lg mx-auto bg-white rounded-2xl p-8 border border-border/50 shadow-lg">
              <h2 className="text-xl font-bold mb-6 text-center" style={{ fontFamily: 'Syne, sans-serif' }}>
                Create Your Partner Account
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name</label>
                  <input
                    type="text"
                    required
                    value={form.full_name}
                    onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Your full name"
                    data-testid="partner-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email Address</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="you@example.com"
                    data-testid="partner-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Country</label>
                  <input
                    type="text"
                    required
                    value={form.country}
                    onChange={(e) => setForm(f => ({ ...f, country: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Your country"
                    data-testid="partner-country-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">PayPal Email Address</label>
                  <input
                    type="email"
                    required
                    value={form.paypal_email}
                    onChange={(e) => setForm(f => ({ ...f, paypal_email: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="paypal@example.com"
                    data-testid="partner-paypal-input"
                  />
                </div>
                <div className="flex items-start gap-2 pt-2">
                  <input
                    type="checkbox"
                    checked={form.agreed_terms}
                    onChange={(e) => setForm(f => ({ ...f, agreed_terms: e.target.checked }))}
                    className="mt-1 accent-primary"
                    data-testid="partner-terms-checkbox"
                  />
                  <label className="text-sm text-muted-foreground">
                    I agree to the Affiliate Terms.
                  </label>
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-full py-5 font-bold"
                  disabled={submitting}
                  data-testid="create-partner-btn"
                >
                  {submitting ? 'Creating...' : 'Create My Partner Account'}
                </Button>
              </form>
            </div>
          </motion.section>
        )}

        {/* Bottom CTA */}
        {!showForm && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-center">
            <Button
              size="lg"
              className="rounded-full px-10 py-6 font-bold text-[15px] shadow-lg shadow-primary/25"
              onClick={() => setShowForm(true)}
              data-testid="bottom-become-partner-cta"
            >
              <ArrowRight className="w-5 h-5 mr-2" />
              Become a Partner
            </Button>
          </motion.div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-border bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-bold text-base mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>DELF Success Partners Program</p>
          <p className="text-sm text-muted-foreground mb-1">Helping students succeed.</p>
          <p className="text-sm text-muted-foreground mb-4">Rewarding those who share success.</p>
          <p className="text-xs text-muted-foreground">&copy; 2026 DELF Success Academy. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PartnersLanding;
